import * as THREE from 'three'
import { extend } from '@react-three/fiber'
import { shaderMaterial } from '@react-three/drei'

// Paul West @prisoner849 https://discourse.threejs.org/u/prisoner849
// https://discourse.threejs.org/t/simple-curved-plane/26647/10
class BentPlaneGeometry extends THREE.PlaneGeometry {
  constructor(radius, ...args) {
    super(...args)
    let p = this.parameters
    let hw = p.width * 0.5
    let a = new THREE.Vector2(-hw, 0)
    let b = new THREE.Vector2(0, radius)
    let c = new THREE.Vector2(hw, 0)
    let ab = new THREE.Vector2().subVectors(a, b)
    let bc = new THREE.Vector2().subVectors(b, c)
    let ac = new THREE.Vector2().subVectors(a, c)
    let r = (ab.length() * bc.length() * ac.length()) / (2 * Math.abs(ab.cross(ac)))
    let center = new THREE.Vector2(0, radius - r)
    let baseV = new THREE.Vector2().subVectors(a, center)
    let baseAngle = baseV.angle() - Math.PI * 0.5
    let arc = baseAngle * 2
    let uv = this.attributes.uv
    let pos = this.attributes.position
    let mainV = new THREE.Vector2()
    for (let i = 0; i < uv.count; i++) {
      let uvRatio = 1 - uv.getX(i)
      let y = pos.getY(i)
      mainV.copy(c).rotateAround(center, arc * uvRatio)
      pos.setXYZ(i, mainV.x, y, mainV.y)
    }
    pos.needsUpdate = true
  }
}

class MeshSineMaterial extends THREE.MeshBasicMaterial {
  constructor(parameters = {}) {
    super(parameters)
    this.setValues(parameters)
    this.time = { value: 0 }
  }
  onBeforeCompile(shader) {
    shader.uniforms.time = this.time
    shader.vertexShader = `
      uniform float time;
      ${shader.vertexShader}
    `
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      `vec3 transformed = vec3(position.x, position.y + sin(time + uv.x * PI * 4.0) / 4.0, position.z);`
    )
  }
}

// Custom material for grayscale to color transition
class ColorTransitionMaterial extends THREE.MeshBasicMaterial {
  constructor(parameters = {}) {
    super(parameters)
    this.setValues(parameters)
    this.colorFactor = { value: 0.0 } // 0 = grayscale, 1 = full color
    this.uniforms = {
      colorFactor: this.colorFactor
    }
    
    // Ensure shader gets recompiled
    this.customProgramCacheKey = () => Math.random()
  }
  
  onBeforeCompile(shader) {
    // Add our uniforms
    shader.uniforms.colorFactor = this.colorFactor
    
    // Add uniform for color transition
    shader.fragmentShader = `
      uniform float colorFactor;
      ${shader.fragmentShader}
    `
    
    // Replace the color calculation to include grayscale transition
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>',
      `
      #include <map_fragment>
      
      #ifdef USE_MAP
        // Convert to grayscale and mix with original color based on colorFactor
        vec3 grayscale = vec3(dot(diffuseColor.rgb, vec3(0.299, 0.587, 0.114)));
        diffuseColor.rgb = mix(grayscale, diffuseColor.rgb, colorFactor);
      #endif
      `
    );
  }
}

// Create a shader material specifically for react-three-fiber
const GrayscaleTransitionMaterial = shaderMaterial(
  // Uniforms
  {
    colorFactor: 0.0,
    map: null
  },
  // Vertex shader
  `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  // Fragment shader
  `
    uniform float colorFactor;
    uniform sampler2D map;
    varying vec2 vUv;
    
    void main() {
      vec4 texColor = texture2D(map, vUv);
      
      // Convert to grayscale
      float gray = dot(texColor.rgb, vec3(0.299, 0.587, 0.114));
      vec3 grayscaleColor = vec3(gray);
      
      // Mix between grayscale and color based on colorFactor
      vec3 finalColor = mix(grayscaleColor, texColor.rgb, colorFactor);
      
      gl_FragColor = vec4(finalColor, texColor.a);
    }
  `
);

// Add Three.js side property to the prototype
GrayscaleTransitionMaterial.prototype.side = THREE.DoubleSide;

extend({ MeshSineMaterial, BentPlaneGeometry, ColorTransitionMaterial, GrayscaleTransitionMaterial })
